import { useState, useEffect, useCallback, useMemo } from 'react';
import {
    useGetInventoryVMInfoQuery,
    useUpdateInventoryStatusAndCriteriaMutation,
    useSendToMigrationMutation,
} from '../../../api/discovery';
import {
    defaultSelectedColumns,
    getLabelFromKey,
    getKeyFromLabel,
    filterValues,
    CPU_FILTER_MAP,
    RAM_FILTER_MAP,
} from './components/InventoryTopBar/constants';

const MIGRATION_READY_VALUES = new Set(Object.values(filterValues));

const parseFilters = (activeFilters) => ({
    migrationReady: activeFilters.filter((f) => MIGRATION_READY_VALUES.has(f)),
    cpu: activeFilters.filter((f) => f in CPU_FILTER_MAP).map((f) => CPU_FILTER_MAP[f]),
    ram: activeFilters.filter((f) => f in RAM_FILTER_MAP).map((f) => RAM_FILTER_MAP[f]),
});
import { useNotificationContext } from '../../../providers/NotificationProvider';
import { useUserCustomization } from '../../../hooks/useUserCustomization';

export const useDiscoveryInventoryData = ({ orgId, searchValue, unassignedOnly = false, initialFilters = [] }) => {
    const [selectedColumns, setSelectedColumns] = useState(defaultSelectedColumns);
    const [initializedSelectedColumns, setInitializedSelectedColumns] = useState(false);
    const { customization, updateColumnOrder, updateSorting } = useUserCustomization();
    // Initialize selected columns from saved customization (only once)
    useEffect(() => {
        if (initializedSelectedColumns) return;
        const savedKeys = customization?.columns || [];
        if (savedKeys.length > 0) {
            const labels = savedKeys.map(getLabelFromKey).filter(Boolean);
            if (labels.length > 0) {
                setSelectedColumns(labels);
            }
        }
        setInitializedSelectedColumns(true);
    }, [customization, initializedSelectedColumns]);

    const [pagination, setPagination] = useState({ current: 1, pageSize: 10, total: 0 });
    const [debouncedSearchValue, setDebouncedSearchValue] = useState('');

    const normalizedInitialFilters = useMemo(
        () => (Array.isArray(initialFilters) ? initialFilters : []),
        [initialFilters],
    );
    const initialFiltersKey = useMemo(() => JSON.stringify(normalizedInitialFilters), [normalizedInitialFilters]);
    const parsedInitial = useMemo(() => parseFilters(normalizedInitialFilters), [initialFiltersKey]);

    const [selectedFilters, setSelectedFilters] = useState(() => normalizedInitialFilters);
    const [migrationReadyFilterValue, setMigrationReadyFilterValue] = useState(() => parsedInitial.migrationReady);
    const [cpuFilterValue, setCpuFilterValue] = useState(() => parsedInitial.cpu);
    const [ramFilterValue, setRamFilterValue] = useState(() => parsedInitial.ram);

    useEffect(() => {
        const parsed = parseFilters(normalizedInitialFilters);
        setSelectedFilters(normalizedInitialFilters);
        setMigrationReadyFilterValue(parsed.migrationReady);
        setCpuFilterValue(parsed.cpu);
        setRamFilterValue(parsed.ram);
        setPagination((prev) => ({ ...prev, current: 1 }));
    }, [initialFiltersKey, normalizedInitialFilters]);

    // Sorting state for sending sorted API requests
    const [sortField, setSortField] = useState(customization?.sorting?.field || null);
    const [sortOrder, setSortOrder] = useState(customization?.sorting?.direction || null);

    const { notifySuccess, notifyError } = useNotificationContext();

    const [cacheKey, setCacheKey] = useState(Date.now());

    // Debounce the incoming searchValue prop
    useEffect(() => {
        const timer = setTimeout(() => {
            setPagination((prev) => ({ ...prev, current: 1 }));
            setDebouncedSearchValue(searchValue);
        }, 500);
        return () => clearTimeout(timer);
    }, [searchValue]);

    const {
        data: inventoryData,
        error: inventoryError,
        isLoading: inventoryLoading,
        isFetching: inventoryFetching,
        refetch: refetchInventoryData,
    } = useGetInventoryVMInfoQuery(
        {
            orgId,
            size: pagination.pageSize,
            page: pagination.current - 1,
            // We can submit multiple values with one query param.
            // e.g. migration_status=ready,not_ready
            ...(migrationReadyFilterValue.length > 0 && {
                migration_ready_status: migrationReadyFilterValue.join(','),
            }),
            ...(cpuFilterValue.length > 0 && { cpu_utilization: cpuFilterValue.join(',') }),
            ...(ramFilterValue.length > 0 && { ram_utilization: ramFilterValue.join(',') }),
            ...(debouncedSearchValue && { combined_search: debouncedSearchValue }),
            ...(sortField && { sort: `${sortField},${sortOrder}` }),
            ...(unassignedOnly && { unassigned: true }),
            _cacheBust: cacheKey, // Force a new request even if the same filters are applied
        },
        {
            skip: !orgId,
            refetchOnMountOrArgChange: true,
            keepUnusedDataFor: 0, // Do not cache data
        },
    );

    // Mutation to update inventory item(s) status and criteria
    const [updateInventoryStatusAndCriteria] = useUpdateInventoryStatusAndCriteriaMutation();

    // Mutation to send inventory items to migrate
    const [sendToMigration] = useSendToMigrationMutation();

    const handleSendToMigration = async (vmIds) => {
        try {
            const result = await sendToMigration({ orgId, vmIds }).unwrap();
            const failedVMErrors = result?.failed_vm_errors || {};
            const failedIds = Object.keys(failedVMErrors);

            if (failedIds.length > 0) {
                const failedVMs = failedIds.map((id) => {
                    const vm = inventoryTableData.find((v) => v.id === id);
                    return {
                        id,
                        name: vm?.name,
                        ipAddress: vm?.ip_addresses?.[0] || 'N/A',
                    };
                });
                if (result?.success_count > 0) {
                    notifySuccess({ message: 'Selected inventory items sent to migration successfully.' });
                }
                setTimeout(() => {
                    refetchInventoryData();
                }, 1000);
                return { failedVMs };
            }

            notifySuccess({ message: 'Selected inventory items sent to migration successfully.' });
            setTimeout(() => {
                refetchInventoryData();
            }, 1000);
            return null;
        } catch (e) {
            notifyError({ message: 'Failed to send selected inventory items to migration.' });
            return null;
        }
    };

    const handleFilterDiscoveryInventoryData = (filterValues) => {
        const parsed = parseFilters(filterValues);
        setMigrationReadyFilterValue(parsed.migrationReady);
        setCpuFilterValue(parsed.cpu);
        setRamFilterValue(parsed.ram);
        setPagination((prev) => ({ ...prev, current: 1 }));
        setCacheKey(Date.now()); // force filter refresh
    };

    const handleColumnSelection = (label, checked) => {
        // Update columns (labels)
        if (checked) {
            setSelectedColumns([...selectedColumns, label]);
        } else {
            setSelectedColumns(selectedColumns.filter((col) => col !== label));
        }
        // Update customization (keys)
        const key = getKeyFromLabel(label);
        const current = customization && customization.columns ? customization.columns : [];
        let next;
        if (checked) {
            next = current.includes(key) ? current : [...current, key];
        } else {
            next = current.filter((k) => k !== key);
        }
        updateColumnOrder(next);
    };

    const handlePaginationChange = useCallback((page, pageSize) => {
        setPagination((prev) => ({ ...prev, current: page, pageSize }));
    }, []);

    const handleSortChange = useCallback(
        (sorting) => {
            const directionMap = { ascend: 'asc', descend: 'desc' };
            setSortField(sorting?.column?.sortKey || null);
            setSortOrder(directionMap[sorting?.order] || null);
            updateSorting(sorting?.column?.sortKey, directionMap[sorting?.order]);
            setCacheKey(Date.now());
        },
        [updateSorting],
    );

    const handleChangeStatus = async (selectedIds, status, criteria) => {
        try {
            await updateInventoryStatusAndCriteria({
                orgId,
                inventoryIds: selectedIds,
                status,
                criteria,
            }).unwrap();
            notifySuccess({ message: 'Inventory status updated successfully.' });
            // Delay refetch to allow backend processing
            setTimeout(() => {
                refetchInventoryData();
            }, 1000);
        } catch (error) {
            notifyError({ message: 'Failed to update inventory status.' });
            console.error('Failed to update inventory status:', error);
        }
    };

    const resetFilters = useCallback(() => {
        setMigrationReadyFilterValue([]);
        setCpuFilterValue([]);
        setRamFilterValue([]);
        setSelectedFilters([]);
        setPagination({ current: 1, pageSize: 10, total: 0 });
    }, []);

    const inventoryTableData = useMemo(() => {
        return inventoryData?.content || [];
    }, [inventoryData]);

    // Update pagination when data changes
    useEffect(() => {
        if (inventoryData?.page) {
            setPagination((prev) => ({
                ...prev,
                total: inventoryData.page.totalElements,
                // page number from the API is 0 indexed, so we need to add 1 to it to match the Pagination UI.
                current: inventoryData.page.number + 1,
            }));
        }
    }, [inventoryData]);

    return {
        // Inventory Query Data
        inventoryTableData,
        inventoryError,
        inventoryLoading,
        inventoryFetching,
        totalElements: inventoryData?.page?.totalElements || 0,

        // Handlers
        handleColumnSelection,
        handleFilterDiscoveryInventoryData,
        handleSendToMigration,
        resetFilters,
        handleSortChange,
        handleChangeStatus,

        // State
        selectedColumns,
        sortField,
        sortOrder,
        selectedFilters,

        // Setters
        setSelectedFilters,

        // Data Fetching
        refetchInventoryData,

        // Pagination
        pagination,
        setPagination,
        handlePaginationChange,
    };
};
